import { As2PartnerDirection, IAs2Partner } from '@core/models/as2-message.model';
import {
    IIntegrationPartnerCreateRequest,
    IIntegrationStatus,
} from '@core/models/integration.model';
import { Supplier } from '@core/models/supplier.model';
import { IUser } from '@core/models/user-listing.model';
import { getSuppliersThunk } from '@redux/features/supplier.slice';
import { getAllUsersThunk } from '@redux/features/user-listing.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import Modal from 'Common/Components/Modal';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import as2IntegrationService from 'services/api/as2-integration.service';
import integrationsService from 'services/api/integrations.service';
import As2EpcisDispatchPanel from '../As2Integration/As2EpcisDispatchPanel';
import {
    canReceiveAs2Partner,
    canSendAs2Partner,
    defaultRouteNames,
    sanitizePartnerToken,
} from '../As2Integration/as2-partner.utils';
import { validateCertFile } from './cert-upload.helpers';
import As2PartnerModal from '../As2Integration/As2PartnerModal';
import {
    formatConnectionTestResult,
    integrationErrorMessage,
} from './integration-user-messages';
import { buildPartnerOnboardingText } from './partner-onboarding';
import { downloadReferenceCertificate } from './cert-upload.helpers';
import WizardSavedPartnersPanel from './WizardSavedPartnersPanel';

const GENERIC_TEMPLATE = 'GENERIC_AS2';

interface Props {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}

type StepMessage = { kind: 'success' | 'error'; text: string } | null;

function getUsersPageContent(raw: unknown): IUser[] {
    const u = raw as Record<string, unknown> | null | undefined;
    if (!u) return [];
    const inner = (u.usersWithPage as Record<string, unknown>) || u;
    return (inner.content as IUser[]) || [];
}

const inputClass =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#bd902e] focus:outline-none focus:ring-2 focus:ring-[#bd902e]/30';

const STEP_LABELS = ['Identity', 'AS2 details', 'Routes & links', 'Save', 'Certificates', 'Verify'];

const IntegrationWizard: FC<Props> = ({ open, onClose, onComplete }) => {
    const dispatch = useAppDispatch();
    const usersRaw = useAppSelector((s: RootState) => s.userListingState.users);
    const { suppliers } = useAppSelector((s: RootState) => (s as RootState).supplierState || { suppliers: [] });

    const pharmacyUsers = useMemo(
        () => getUsersPageContent(usersRaw).filter((u) => u.account?.accountType === 'PHARMACY'),
        [usersRaw]
    );

    const [step, setStep] = useState(0);
    const [templateReady, setTemplateReady] = useState(false);
    const [direction, setDirection] = useState<As2PartnerDirection>('BIDIRECTIONAL');
    const [partnerName, setPartnerName] = useState('');
    const [partnerGroupCode, setPartnerGroupCode] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactEmailProd, setContactEmailProd] = useState('');
    const [as2Id, setAs2Id] = useState('');
    const [as2IdProd, setAs2IdProd] = useState('');
    const [as2UrlTest, setAs2UrlTest] = useState('');
    const [as2UrlProd, setAs2UrlProd] = useState('');
    const [openas2PartnerNameProd, setOpenas2PartnerNameProd] = useState('');
    const [integrationStatus, setIntegrationStatus] = useState<IIntegrationStatus | null>(null);
    const [saveSucceeded, setSaveSucceeded] = useState(false);
    const [progressHint, setProgressHint] = useState<string | null>(null);
    const [openas2PartnerName, setOpenas2PartnerName] = useState('');
    const [keystoreAlias, setKeystoreAlias] = useState('');
    const [partnerIp, setPartnerIp] = useState('');
    const [pharmacyUserId, setPharmacyUserId] = useState<number | undefined>();
    const [supplierId, setSupplierId] = useState<number | undefined>();
    const [savedPartners, setSavedPartners] = useState<IAs2Partner[]>([]);
    const [testCertFile, setTestCertFile] = useState<File | null>(null);
    const [prodCertFile, setProdCertFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [testingPartnerCode, setTestingPartnerCode] = useState<string | null>(null);
    const [stepMessage, setStepMessage] = useState<StepMessage>(null);
    /** When true, do not overwrite OpenAS2 name / keystore alias from display name. */
    const [openas2IdentityTouched, setOpenas2IdentityTouched] = useState(false);
    const [editPartner, setEditPartner] = useState<IAs2Partner | null>(null);

    const routes = useMemo(() => defaultRouteNames(partnerName), [partnerName]);
    const partnerToken = useMemo(() => sanitizePartnerToken(partnerName), [partnerName]);

    const reset = () => {
        setStep(0);
        setTemplateReady(false);
        setDirection('BIDIRECTIONAL');
        setPartnerName('');
        setPartnerGroupCode('');
        setContactEmail('');
        setContactEmailProd('');
        setAs2Id('');
        setAs2IdProd('');
        setAs2UrlTest('');
        setAs2UrlProd('');
        setIntegrationStatus(null);
        setSaveSucceeded(false);
        setProgressHint(null);
        setOpenas2PartnerName('');
        setOpenas2PartnerNameProd('');
        setKeystoreAlias('');
        setPartnerIp('');
        setPharmacyUserId(undefined);
        setSupplierId(undefined);
        setSavedPartners([]);
        setTestCertFile(null);
        setProdCertFile(null);
        setStepMessage(null);
        setOpenas2IdentityTouched(false);
    };

    useEffect(() => {
        if (!open) {
            reset();
            return;
        }
        dispatch(getAllUsersThunk({ pageNumber: '0', pageSize: '500', accountType: 'PHARMACY' }));
        dispatch(getSuppliersThunk());
        integrationsService
            .getStatus()
            .then((r: { data?: { integrationStatus?: IIntegrationStatus } }) => {
                setIntegrationStatus(r.data?.integrationStatus ?? null);
            })
            .catch(() => setIntegrationStatus(null));
        integrationsService
            .listTemplatesForWizard()
            .then((r: any) => {
                const list = r.data.templates || [];
                const hasGeneric = list.some((t: { code: string }) => t.code === GENERIC_TEMPLATE);
                if (!hasGeneric) {
                    const msg =
                        'AS2 partner template not found — check database migrations and backend.';
                    setStepMessage({ kind: 'error', text: msg });
                    toast.error(msg, { autoClose: 5000 });
                    setTemplateReady(false);
                } else {
                    setTemplateReady(true);
                }
            })
            .catch((err) => {
                setTemplateReady(false);
                const msg = integrationErrorMessage(err, 'Could not load integration templates.');
                setStepMessage({ kind: 'error', text: msg });
                toast.error(msg, { autoClose: 5000 });
            });
    }, [open, dispatch]);

    /** Suggest OpenAS2 name / alias from display name once per wizard open (not when user clears the field). */
    useEffect(() => {
        if (!partnerToken || openas2IdentityTouched) {
            return;
        }
        setOpenas2PartnerName(partnerToken);
        setKeystoreAlias(partnerToken.toLowerCase());
    }, [partnerToken, openas2IdentityTouched]);

    const validateStep = (s: number): boolean => {
        if (s === 0) {
            if (!partnerGroupCode.trim() || !partnerName.trim()) {
                toast.error('Group code and display name are required', { autoClose: 5000 });
                return false;
            }
            if (!contactEmail.trim() || !contactEmail.includes('@')) {
                toast.error('Partner contact email is required', { autoClose: 5000 });
                return false;
            }
            if (!templateReady) {
                toast.error('Wizard not ready', { autoClose: 5000 });
                return false;
            }
        }
        if (s === 1) {
            if (!as2Id.trim()) {
                toast.error('Partner AS2 ID is required', { autoClose: 5000 });
                return false;
            }
            if (!openas2PartnerName.trim()) {
                toast.error('OpenAS2 partner name is required', { autoClose: 5000 });
                return false;
            }
            if (!keystoreAlias.trim()) {
                toast.error('Keystore alias is required', { autoClose: 5000 });
                return false;
            }
            if (canSendAs2Partner(direction) && !as2UrlTest.trim()) {
                toast.error('Partner pre-go-live test AS2 URL is required for SEND and BIDIRECTIONAL', {
                    autoClose: 5000,
                });
                return false;
            }
            if (canSendAs2Partner(direction) && !as2UrlProd.trim()) {
                toast.error('Partner production AS2 URL is required for SEND and BIDIRECTIONAL', { autoClose: 5000 });
                return false;
            }
        }
        // Step 2 (pharmacy/supplier links) is optional — no required validation.
        return true;
    };

    const savePair = async () => {
        setSaving(true);
        setStepMessage(null);
        setProgressHint('Writing partner records and partnerships.xml…');
        try {
            const body: IIntegrationPartnerCreateRequest = {
                templateCode: GENERIC_TEMPLATE,
                partnerGroupCode,
                partnerName,
                contactEmail: contactEmail.trim(),
                contactEmailProd: contactEmailProd.trim() || undefined,
                direction,
                as2Id: as2Id.trim(),
                as2IdProd: as2IdProd.trim() || undefined,
                as2UrlTest: canSendAs2Partner(direction) ? as2UrlTest.trim() : undefined,
                as2UrlProd: canSendAs2Partner(direction) ? as2UrlProd.trim() : undefined,
                openas2PartnerName: openas2PartnerName.trim() || partnerToken,
                openas2PartnerNameProd: openas2PartnerNameProd.trim() || undefined,
                keystoreAlias: keystoreAlias.trim() || partnerToken.toLowerCase(),
                partnerIp: partnerIp.trim() || undefined,
                pharmacyUserId: canSendAs2Partner(direction) ? pharmacyUserId : undefined,
                supplierId: canReceiveAs2Partner(direction) ? supplierId : undefined,
            };
            const r: any = await integrationsService.createPartnerPair(body);
            const partners: IAs2Partner[] = r.data.partners || [];
            setSavedPartners(partners);
            setSaveSucceeded(true);
            const msg =
                'Configuration saved. You can close this window and return anytime — finish certificates and testing here or under AS2 Partners.';
            setStepMessage({ kind: 'success', text: msg });
            toast.success('Partner configuration saved', { autoClose: 5000 });
            setStep(4);
        } catch (err) {
            const msg = integrationErrorMessage(err, 'Could not save partner configuration.');
            setStepMessage({ kind: 'error', text: msg });
            toast.error(msg, { autoClose: 5000 });
        } finally {
            setSaving(false);
            setProgressHint(null);
        }
    };

    const uploadCerts = async () => {
        const test = savedPartners.find((p) => p.code?.endsWith('_TEST') || p.environment === 'TEST');
        const prod = savedPartners.find((p) => !p.code?.endsWith('_TEST') && p.environment === 'PRODUCTION');
        if (testCertFile) {
            const err = validateCertFile(testCertFile);
            if (err) {
                toast.error(err, { autoClose: 8000 });
                return;
            }
        }
        if (prodCertFile) {
            const err = validateCertFile(prodCertFile);
            if (err) {
                toast.error(err, { autoClose: 8000 });
                return;
            }
        }
        if (!testCertFile && !prodCertFile) {
            toast.info('No files selected — you can upload certificates later from AS2 Partners.', {
                autoClose: 6000,
            });
            setStep(5);
            return;
        }
        if (!test?.code || !prod?.code) {
            toast.error('Partner codes missing — save configuration first.', { autoClose: 5000 });
            return;
        }
        setSaving(true);
        setStepMessage(null);
        setProgressHint('Updating keystore and partnerships.xml…');
        try {
            await integrationsService.uploadCertificatesPair(
                test.code,
                prod.code,
                testCertFile,
                prodCertFile
            );
            const msg = 'Certificates uploaded';
            setStepMessage({ kind: 'success', text: msg });
            toast.success(msg, { autoClose: 5000 });
            setStep(5);
        } catch (err) {
            const msg = integrationErrorMessage(
                err,
                'Could not upload certificates. Check AS2 Partners — one environment may have saved.'
            );
            setStepMessage({ kind: 'error', text: msg });
            toast.error(msg, { autoClose: 5000 });
        } finally {
            setSaving(false);
            setProgressHint(null);
        }
    };

    const testConnection = async (code: string) => {
        setTestingPartnerCode(code);
        setStepMessage(null);
        try {
            const r: any = await as2IntegrationService.testConnection(code);
            const formatted = formatConnectionTestResult(code, r.data);
            setStepMessage({ kind: formatted.kind, text: formatted.message });
            if (formatted.kind === 'success') {
                toast.success(formatted.message, { autoClose: 8000 });
                if (formatted.info) {
                    toast.info(formatted.info, { autoClose: 12000 });
                }
            } else {
                toast.error(formatted.message, { autoClose: 10000 });
            }
        } catch (err) {
            const msg = integrationErrorMessage(err, 'Could not run connection test.');
            setStepMessage({ kind: 'error', text: msg });
            toast.error(msg, { autoClose: 5000 });
        } finally {
            setTestingPartnerCode(null);
        }
    };

    const reloadSavedPartners = async () => {
        if (savedPartners.length === 0) return;
        try {
            const r: any = await as2IntegrationService.listPartners();
            const all: IAs2Partner[] = r.data.as2Partners || [];
            const codes = new Set(savedPartners.map((p) => p.code));
            setSavedPartners(all.filter((p) => p.code && codes.has(p.code)));
        } catch {
            /* ignore */
        }
    };

    const copyOnboardingPacket = async () => {
        const text = buildPartnerOnboardingText(integrationStatus, savedPartners, routes, {
            partnerGroupCode,
            as2Id,
            as2UrlTest,
            as2UrlProd,
            openas2PartnerName,
            contactEmail,
        });
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Onboarding packet copied to clipboard', { autoClose: 4000 });
        } catch {
            toast.error('Could not copy — select and copy manually from the preview below.', {
                autoClose: 6000,
            });
        }
    };

    const enableProductionPartner = async () => {
        const prod = savedPartners.find(
            (p) => p.environment === 'PRODUCTION' || (p.code && !p.code.endsWith('_TEST'))
        );
        if (!prod?.code) return;
        if (prod.enabled) {
            toast.info(`${prod.code} is already enabled`, { autoClose: 4000 });
            return;
        }
        try {
            await as2IntegrationService.setEnabled(prod.code, true);
            toast.success(`${prod.code} enabled for production`, { autoClose: 5000 });
            await reloadSavedPartners();
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not enable production partner.'), {
                autoClose: 5000,
            });
        }
    };

    const markTestPassed = async () => {
        const test = savedPartners.find((p) => p.code?.endsWith('_TEST'));
        if (!test?.code) return;
        setStepMessage(null);
        try {
            await integrationsService.markTestPassed(test.code);
            toast.success('Test marked as passed', { autoClose: 5000 });
            onComplete();
            onClose();
        } catch (err) {
            const msg = integrationErrorMessage(err, 'Could not mark test as passed.');
            setStepMessage({ kind: 'error', text: msg });
            toast.error(msg, { autoClose: 5000 });
        }
    };

    const testPartner = savedPartners.find((p) => p.code?.endsWith('_TEST'));
    const prodPartner = savedPartners.find(
        (p) => p.environment === 'PRODUCTION' || (p.code && !p.code.endsWith('_TEST'))
    );
    const selectedSupplier = useMemo(
        () => (suppliers || []).find((s: Supplier) => s.id === supplierId),
        [suppliers, supplierId]
    );
    const supplierMissingSgln =
        canReceiveAs2Partner(direction) &&
        selectedSupplier &&
        !!selectedSupplier.supplierGln?.trim() &&
        !selectedSupplier.supplierSgln?.trim();

    if (!open) return null;

    return (
        <>
        <Modal
            show={open}
            onHide={onClose}
            dialogClassName="mx-auto w-full max-w-[calc(100vw-1rem)] sm:max-w-[720px] p-4 shadow"
        >
            <Modal.Header className="shrink-0 border-b border-slate-200 pb-3">
                <Modal.Title className="text-lg font-semibold text-slate-800">Add AS2 partner</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-4">
                <div className="mb-4 flex flex-wrap gap-2 text-xs text-slate-500">
                    {STEP_LABELS.map((label, i) => (
                        <span key={label} className={step === i ? 'font-semibold text-[#bd902e]' : ''}>
                            {i + 1}. {label}
                        </span>
                    ))}
                </div>

                {stepMessage && (
                    <p
                        className={`mb-4 rounded-md border p-3 text-sm ${
                            stepMessage.kind === 'success'
                                ? 'border-green-200 bg-green-50 text-green-900'
                                : 'border-red-200 bg-red-50 text-red-900'
                        }`}
                    >
                        {stepMessage.text}
                    </p>
                )}

                {progressHint && (
                    <p className="mb-4 text-sm text-slate-600">{progressHint}</p>
                )}

                {integrationStatus && integrationStatus.as2Enabled === false && (
                    <p className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                        AS2 is disabled in server configuration. Enable <code>as2.enabled</code> before partners
                        can send or receive.
                    </p>
                )}

                {saveSucceeded && savedPartners.length > 0 && step >= 4 && (
                    <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
                        <p className="font-medium">Configuration saved</p>
                        <p className="mt-1">
                            You can close this window and continue later from{' '}
                            <Link to={PRIVATEPATHS.As2Partners} className="text-[#bd902e] hover:underline">
                                AS2 Partners
                            </Link>
                            .
                        </p>
                        <ul className="mt-2 list-inside list-disc text-xs">
                            {savedPartners.map((p) => (
                                <li key={p.code}>
                                    <strong>{p.code}</strong> — upload certificates, complete go-live checks, enable
                                    production when ready (AS2 Partners / Messages)
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {saveSucceeded && savedPartners.length > 0 && step >= 4 && (
                    <WizardSavedPartnersPanel
                        partners={savedPartners}
                        contactEmail={contactEmail}
                        onRefresh={reloadSavedPartners}
                        onEdit={(p) => setEditPartner(p)}
                    />
                )}

                {step === 0 && (
                    <div className="space-y-3">
                        <p className="text-sm text-slate-600">
                            Onboard a <strong>production</strong> trading partner for AS2/EPCIS. Configure production
                            URLs and certificates, complete go-live checks, then <strong>enable</strong> the production
                            partner for live traffic. A companion test record is created automatically for pre-go-live
                            MDN validation only.
                        </p>
                        <label className="block text-sm font-medium text-slate-700">Direction *</label>
                        <select
                            className={inputClass}
                            value={direction}
                            onChange={(e) => setDirection(e.target.value as As2PartnerDirection)}
                        >
                            <option value="RECEIVE">RECEIVE — partner sends to Antar</option>
                            <option value="SEND">SEND — Antar sends to partner</option>
                            <option value="BIDIRECTIONAL">BIDIRECTIONAL — both</option>
                        </select>
                        {direction === 'SEND' && (
                            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                Antar sends EPCIS to the partner&apos;s AS2 URL. You need their certificate and AS2 ID.
                            </p>
                        )}
                        {direction === 'RECEIVE' && (
                            <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                                Partner sends to Antar&apos;s inbound URL. Share Antar AS2 ID + cert with the partner.
                            </p>
                        )}
                        {(integrationStatus?.as2InboundUrlTest ||
                            integrationStatus?.as2InboundUrlProd) && (
                            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800">
                                <p className="font-medium">Production inbound URL (share with partners)</p>
                                {integrationStatus.as2InboundUrlProd && (
                                    <p className="mt-1 break-all font-medium text-slate-900">
                                        {integrationStatus.as2InboundUrlProd}
                                    </p>
                                )}
                                {integrationStatus.as2InboundUrlTest && (
                                    <p className="mt-2 break-all text-slate-600">
                                        <span className="font-medium">Pre-go-live test URL:</span>{' '}
                                        {integrationStatus.as2InboundUrlTest}
                                    </p>
                                )}
                            </div>
                        )}
                        <input
                            className={inputClass}
                            placeholder="Partner group code (e.g. ACME_PHARM)"
                            value={partnerGroupCode}
                            onChange={(e) => setPartnerGroupCode(e.target.value.toUpperCase())}
                        />
                        <input
                            className={inputClass}
                            placeholder="Display name"
                            value={partnerName}
                            onChange={(e) => setPartnerName(e.target.value)}
                        />
                        <input
                            className={inputClass}
                            type="email"
                            placeholder="Partner contact email (test) *"
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                        />
                        <input
                            className={inputClass}
                            type="email"
                            placeholder="Partner contact email (production) — leave blank to reuse test"
                            value={contactEmailProd}
                            onChange={(e) => setContactEmailProd(e.target.value)}
                        />
                    </div>
                )}

                {step === 1 && (
                    <div className="space-y-3">
                        <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                            Production partner <strong>{partnerGroupCode || 'GROUP'}</strong> is the live record.
                            Pre-go-live partner <strong>{partnerGroupCode || 'GROUP'}_TEST</strong> is created for MDN
                            checks before you enable production. Use the same AS2 ID, OpenAS2 name, and keystore alias
                            unless your partner uses different test and production identities.
                        </p>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-800">
                                Partner AS2 ID (test) *
                            </label>
                            <input
                                className={inputClass}
                                placeholder="e.g. ACME_AS2_TEST"
                                value={as2Id}
                                onChange={(e) => setAs2Id(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-800">
                                Partner AS2 ID (production)
                            </label>
                            <input
                                className={inputClass}
                                placeholder="e.g. ACME_AS2_PROD — leave blank to reuse test"
                                value={as2IdProd}
                                onChange={(e) => setAs2IdProd(e.target.value)}
                            />
                        </div>
                        {canSendAs2Partner(direction) && (
                            <>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-800">
                                        Partner AS2 URL (production) *
                                    </label>
                                    <input
                                        className={inputClass}
                                        placeholder="https://as2.partner.com/as2/..."
                                        value={as2UrlProd}
                                        onChange={(e) => setAs2UrlProd(e.target.value)}
                                    />
                                    <p className="mt-1 text-xs text-slate-500">
                                        Live outbound endpoint for <strong>{partnerGroupCode || 'GROUP'}</strong>{' '}
                                        (enable this partner after go-live checks).
                                    </p>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-800">
                                        Partner AS2 URL (pre-go-live test) *
                                    </label>
                                    <input
                                        className={inputClass}
                                        placeholder="https://as2-test.partner.com/as2/..."
                                        value={as2UrlTest}
                                        onChange={(e) => setAs2UrlTest(e.target.value)}
                                    />
                                    <p className="mt-1 text-xs text-slate-500">
                                        Used only on <strong>{partnerGroupCode || 'GROUP'}_TEST</strong> for MDN /
                                        test EPCIS before production is enabled.
                                    </p>
                                </div>
                            </>
                        )}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-800">
                                OpenAS2 partner name *
                            </label>
                            <input
                                className={inputClass}
                                placeholder="e.g. AcmePharmacy"
                                value={openas2PartnerName}
                                onChange={(e) => {
                                    setOpenas2IdentityTouched(true);
                                    setOpenas2PartnerName(e.target.value);
                                }}
                            />
                            {openas2PartnerName.trim() &&
                                openas2PartnerName === openas2PartnerName.toLowerCase() && (
                                    <p className="mt-1 text-xs text-amber-800">
                                        Tip: use PascalCase (e.g. AcmePharmacy) so the name matches OpenAS2 partner
                                        conventions in generated partnerships.xml.
                                    </p>
                                )}
                            {partnerToken && (
                                <button
                                    type="button"
                                    className="mt-1 text-xs text-[#bd902e] hover:underline"
                                    onClick={() => {
                                        setOpenas2PartnerName(partnerToken);
                                        setKeystoreAlias(partnerToken.toLowerCase());
                                        setOpenas2IdentityTouched(false);
                                    }}
                                >
                                    Suggest from display name ({partnerToken})
                                </button>
                            )}
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-800">
                                OpenAS2 partner name (production)
                            </label>
                            <input
                                className={inputClass}
                                placeholder="leave blank to reuse test name"
                                value={openas2PartnerNameProd}
                                onChange={(e) => setOpenas2PartnerNameProd(e.target.value)}
                            />
                            <p className="mt-1 text-xs text-slate-500">
                                Must be unique in partnerships.xml. Set this only if the partner uses a different
                                production OpenAS2 identity (e.g. AcmePharmacy vs AcmePharmacyProd).
                            </p>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-800">
                                Keystore alias *
                            </label>
                            <input
                                className={inputClass}
                                placeholder="e.g. acmepharmacy"
                                value={keystoreAlias}
                                onChange={(e) => {
                                    setOpenas2IdentityTouched(true);
                                    setKeystoreAlias(e.target.value);
                                }}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-800">
                                Partner IP (optional)
                            </label>
                            <input
                                className={inputClass}
                                placeholder="Partner IP (optional)"
                                value={partnerIp}
                                onChange={(e) => setPartnerIp(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-3">
                        <div className="admin-motion-card rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                            <p className="mb-2 font-medium">Auto-generated OpenAS2 routes</p>
                            <p className="mb-2 text-slate-600">
                                These names are written into generated partnerships.xml. To change them, use a new
                                partner group code in the wizard — do not edit the XML file on disk.
                            </p>
                            {canSendAs2Partner(direction) && (
                                <>
                                    <p>Production outbound: {routes.prodOutbound}</p>
                                    <p>Pre-go-live test outbound: {routes.testOutbound}</p>
                                </>
                            )}
                            {canReceiveAs2Partner(direction) && (
                                <>
                                    <p>Production inbound: {routes.prodInbound}</p>
                                    <p>Pre-go-live test inbound: {routes.testInbound}</p>
                                </>
                            )}
                        </div>
                        {canSendAs2Partner(direction) && (
                            <select
                                className={inputClass}
                                value={pharmacyUserId ?? ''}
                                onChange={(e) =>
                                    setPharmacyUserId(e.target.value ? Number(e.target.value) : undefined)
                                }
                            >
                                <option value="">— Linked pharmacy * —</option>
                                {pharmacyUsers.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.account?.businessName
                                            ? `${u.account.businessName} (${u.email})`
                                            : u.email}
                                    </option>
                                ))}
                            </select>
                        )}
                        {canReceiveAs2Partner(direction) && (
                            <select
                                className={inputClass}
                                value={supplierId ?? ''}
                                onChange={(e) =>
                                    setSupplierId(e.target.value ? Number(e.target.value) : undefined)
                                }
                            >
                                <option value="">— Linked supplier * —</option>
                                {(suppliers || []).map((s: Supplier) => (
                                    <option key={s.id} value={s.id}>
                                        {s.businessName || s.email}
                                    </option>
                                ))}
                            </select>
                        )}
                        {supplierMissingSgln && (
                            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                Selected supplier has GLN but no SGLN. Inbound EPCIS may fail sender matching — set{' '}
                                <strong>supplier SGLN</strong> on the supplier record before go-live.
                            </p>
                        )}
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-3 text-sm text-slate-700">
                        <p>
                            Saves production partner <strong>{partnerGroupCode}</strong> and pre-go-live{' '}
                            <strong>{partnerGroupCode}_TEST</strong> with direction <strong>{direction}</strong>.
                        </p>
                        <ul className="list-inside list-disc text-xs text-slate-600">
                            <li>AS2 ID (test): {as2Id}</li>
                            <li>AS2 ID (production): {as2IdProd.trim() || as2Id}</li>
                            {canSendAs2Partner(direction) && (
                                <>
                                    <li>Production URL: {as2UrlProd}</li>
                                    <li>Pre-go-live test URL: {as2UrlTest}</li>
                                </>
                            )}
                            <li>Contact (test): {contactEmail}</li>
                            <li>Contact (production): {contactEmailProd.trim() || contactEmail}</li>
                            <li>OpenAS2 name (test): {openas2PartnerName.trim() || partnerToken}</li>
                            <li>
                                OpenAS2 name (production):{' '}
                                {openas2PartnerNameProd.trim() || openas2PartnerName.trim() || partnerToken}
                            </li>
                        </ul>
                    </div>
                )}

                {step === 4 && (
                    <div className="space-y-3 text-sm">
                        <p className="text-slate-600">
                            Upload partner X.509 certificates (.cer, .pem, .crt). Give partners your Antar public
                            cert (download below) plus inbound URLs from step 1.
                        </p>
                        {saveSucceeded && prodPartner?.code && (
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
                                    onClick={() => downloadReferenceCertificate(prodPartner.code!)}
                                >
                                    Download Antar cert (production)
                                </button>
                                {testPartner?.code && (
                                    <button
                                        type="button"
                                        className="rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50"
                                        onClick={() => downloadReferenceCertificate(testPartner.code!)}
                                    >
                                        Download Antar cert (pre-go-live test)
                                    </button>
                                )}
                            </div>
                        )}
                        <div>
                            <label className="mb-1 block text-xs font-medium">
                                Production certificate ({prodPartner?.code || 'production'}) *
                            </label>
                            <input
                                type="file"
                                accept=".cer,.pem,.crt,.p12,.pfx"
                                onChange={(e) => setProdCertFile(e.target.files?.[0] || null)}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-slate-600">
                                Pre-go-live test certificate ({testPartner?.code || 'test'})
                            </label>
                            <input
                                type="file"
                                accept=".cer,.pem,.crt,.p12,.pfx"
                                onChange={(e) => setTestCertFile(e.target.files?.[0] || null)}
                            />
                        </div>
                    </div>
                )}

                {step === 5 && (
                    <div className="space-y-3 text-sm">
                        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800">
                            <p className="mb-2 font-medium">Go-live checklist</p>
                            <ol className="list-inside list-decimal space-y-1 text-slate-700">
                                {canSendAs2Partner(direction) && (
                                    <>
                                        <li>Upload production certificate and share production inbound URL with partner</li>
                                        <li>Run pre-go-live checks on {partnerGroupCode || 'GROUP'}_TEST (connection + test EPCIS + MDN)</li>
                                        <li>Mark test passed, then enable production partner {partnerGroupCode || 'GROUP'} for live sends</li>
                                    </>
                                )}
                                {canReceiveAs2Partner(direction) && (
                                    <li>
                                        Partner sends test EPCIS to your inbound URL → review in{' '}
                                        <Link to={PRIVATEPATHS.As2Messages} className="text-[#bd902e] hover:underline">
                                            AS2 Messages
                                        </Link>{' '}
                                        (serials + approve)
                                    </li>
                                )}
                            </ol>
                            <button
                                type="button"
                                className="mt-2 text-xs font-medium text-[#bd902e] hover:underline"
                                onClick={copyOnboardingPacket}
                            >
                                Copy onboarding packet for partner
                            </button>
                        </div>
                        {canSendAs2Partner(direction) && (
                            <>
                                <p className="text-slate-600">
                                    Pre-go-live: test reachability on <strong>{testPartner?.code}</strong>. After
                                    enable, live traffic uses <strong>{prodPartner?.code}</strong>.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {prodPartner?.code && (
                                        <button
                                            type="button"
                                            disabled={!!testingPartnerCode}
                                            onClick={() => testConnection(prodPartner.code!)}
                                            className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
                                        >
                                            {testingPartnerCode === prodPartner.code
                                                ? 'Testing…'
                                                : `Production URL — ${prodPartner.code}`}
                                        </button>
                                    )}
                                    {testPartner?.code && (
                                        <button
                                            type="button"
                                            disabled={!!testingPartnerCode}
                                            onClick={() => testConnection(testPartner.code!)}
                                            className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
                                        >
                                            {testingPartnerCode === testPartner.code
                                                ? 'Testing…'
                                                : `Pre-go-live URL — ${testPartner.code}`}
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-slate-500">
                                    After send test: open{' '}
                                    <Link to={PRIVATEPATHS.As2Messages} className="text-[#bd902e] hover:underline">
                                        AS2 Messages
                                    </Link>{' '}
                                    and confirm MDN_RECEIVED (~30s).
                                </p>
                                <As2EpcisDispatchPanel
                                    partners={savedPartners}
                                    environmentFilter="TEST"
                                    lockEnvironmentFilter
                                    defaultPartnerCode={testPartner?.code}
                                />
                                {prodPartner?.code && !prodPartner.enabled && (
                                    <button
                                        type="button"
                                        onClick={enableProductionPartner}
                                        className="rounded border border-green-600 bg-green-50 px-3 py-1.5 text-sm text-green-900 hover:bg-green-100"
                                    >
                                        Enable production — {prodPartner.code}
                                    </button>
                                )}
                            </>
                        )}
                        {canReceiveAs2Partner(direction) && (
                            <p className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                                Inbound files land in OpenAS2 inbox, then appear under{' '}
                                <Link to={PRIVATEPATHS.As2Messages} className="text-[#bd902e] hover:underline">
                                    AS2 Messages
                                </Link>
                                . Complete staging (including serial numbers) and approve to create a batch.
                            </p>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-3">
                <ButtonBase innerText="Cancel" onClick={onClose} className="!bg-slate-200 !text-slate-800" />
                <div className="flex gap-2">
                    {step > 0 && step < 5 && (
                        <button
                            type="button"
                            onClick={() => setStep((s) => s - 1)}
                            className="rounded border border-slate-300 px-3 py-1.5 text-sm"
                        >
                            Back
                        </button>
                    )}
                    {step < 3 && (
                        <ButtonBase
                            innerText="Next"
                            onClick={() => {
                                if (validateStep(step)) setStep((s) => s + 1);
                            }}
                            disabled={step === 0 && !templateReady}
                        />
                    )}
                    {step === 3 && (
                        <>
                            {stepMessage?.kind === 'error' && (
                                <button
                                    type="button"
                                    onClick={savePair}
                                    disabled={saving}
                                    className="rounded border border-slate-300 px-3 py-1.5 text-sm"
                                >
                                    Retry save
                                </button>
                            )}
                            <ButtonBase
                                innerText={saving ? 'Saving…' : 'Save & continue'}
                                onClick={savePair}
                                disabled={saving}
                            />
                        </>
                    )}
                    {step === 4 && (
                        <>
                            <ButtonBase
                                innerText="Skip for now"
                                onClick={() => setStep(5)}
                                className="!bg-slate-200 !text-slate-800"
                            />
                            {stepMessage?.kind === 'error' && (
                                <button
                                    type="button"
                                    onClick={uploadCerts}
                                    disabled={saving}
                                    className="rounded border border-slate-300 px-3 py-1.5 text-sm"
                                >
                                    Retry upload
                                </button>
                            )}
                            <ButtonBase
                                innerText={saving ? 'Uploading…' : 'Upload & continue'}
                                onClick={uploadCerts}
                                disabled={saving || (!testCertFile && !prodCertFile)}
                            />
                        </>
                    )}
                    {step === 5 && (
                        <>
                            {canSendAs2Partner(direction) && (
                                <ButtonBase innerText="Mark test passed" onClick={markTestPassed} />
                            )}
                            <ButtonBase innerText="Done" onClick={() => { onComplete(); onClose(); }} />
                        </>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
            <As2PartnerModal
                open={!!editPartner}
                existing={editPartner}
                onClose={() => setEditPartner(null)}
                onSaved={() => {
                    reloadSavedPartners();
                    setEditPartner(null);
                }}
            />
        </>
    );
};

export default IntegrationWizard;
